import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { EndOfLifeWishesData, endOfLifeWishesService } from '../../../../services/endOfLifeWishesInfo';
import { tasksService } from '../../../../services/tasks';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('EndOfLifeWishesData');

type EndOfLifeWishesDataScreenRouteProp = RouteProp<RootStackParamList, 'EndOfLifeWishesData'>;

type InfoItemProps = {
  label: string;
  value: string | boolean | undefined;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => {
  let displayValue = '';
  if (typeof value === 'boolean') {
    displayValue = value ? 'Yes' : 'No';
  } else if (typeof value === 'string') {
    displayValue = value || 'Not specified';
  } else {
    displayValue = 'Not specified';
  }

  return (
    <View style={styles.infoItem}>
      <Text style={styles.label}>{label}</Text>
      <Text style={styles.value}>{displayValue}</Text>
      <View style={styles.separator} />
    </View>
  );
};

export const EndOfLifeWishesDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<EndOfLifeWishesDataScreenRouteProp>();
  const [wishesInfo, setWishesInfo] = useState<EndOfLifeWishesData | null>(null);
  const [loading, setLoading] = useState(true);

  const endOfLifeWishesId = route.params?.endOfLifeWishesId;
  const targetUserId = route.params?.targetUserId;
  const viewOnly = route.params?.viewOnly;

  useEffect(() => {
    loadWishesInfo();
  }, []);

  const loadWishesInfo = async () => {
    try {
      if (endOfLifeWishesId) {
        const info = await endOfLifeWishesService.getEndOfLifeWishesById(endOfLifeWishesId, targetUserId);
        setWishesInfo(info);
      } else {
        const allInfo = await endOfLifeWishesService.getAllEndOfLifeWishes(targetUserId);
        if (allInfo.length > 0) {
          setWishesInfo(allInfo[0]);
        }
      }
    } catch (error) {
      logger.error('Error loading end-of-life wishes info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (wishesInfo) {
      navigation.navigate('EndOfLifeWishesInput', {
        endOfLifeWishesData: wishesInfo,
        isEditing: true
      });
    } else {
      navigation.navigate('EndOfLifeWishesInput');
    }
  };

  const handleDelete = () => {
    if (wishesInfo) {
      Alert.alert(
        'Delete End-of-Life Wishes',
        'Are you sure you want to delete this information? This action cannot be undone.',
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: async () => {
              try {
                await endOfLifeWishesService.deleteEndOfLifeWishes(wishesInfo.id!);
                const remainingData = await endOfLifeWishesService.getAllEndOfLifeWishes();
                if (!remainingData || remainingData.length === 0) {
                  await tasksService.resetTask('end_of_life_wishes');
                }
                (navigation as any).navigate('CarePreferences', { screen: 'CarePreferencesChecklist', params: { refreshKey: Date.now() } });
              } catch (error) {
                logger.error('Error deleting end-of-life wishes info:', error);
                Alert.alert('Error', 'Failed to delete end-of-life wishes');
              }
            },
          },
        ]
      );
    }
  };

  const handleLockUnlock = () => {
    if (wishesInfo) {
      navigation.navigate('EndOfLifeWishesLockUnlock', { endOfLifeWishesId: wishesInfo.id });
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
          <Image source={require('../../../../assets/icons/back.png')} style={[styles.backIcon, { tintColor: '#fff' }]} />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image source={require('../../../../assets/icons/keyimage.png')} style={styles.backgroundKey} />
        <Image source={require('../../../../assets/logo.png')} style={styles.logo} resizeMode="contain" />
        <Text style={styles.title}>End-of-Life Treatment Wishes</Text>
      </View>

      <View style={styles.infoContainer}>
        <ScrollView style={styles.scrollView}>
          <Text style={styles.sectionTitle}>Ventilator/Life Support</Text>
          <InfoItem label="Preference" value={wishesInfo?.ventilatorPreference} />
          {wishesInfo?.ventilatorTimeLimit && <InfoItem label="Time Limit" value={wishesInfo.ventilatorTimeLimit} />}
          {wishesInfo?.ventilatorNotes && <InfoItem label="Notes" value={wishesInfo.ventilatorNotes} />}

          <Text style={styles.sectionTitle}>Feeding Tube</Text>
          <InfoItem label="Preference" value={wishesInfo?.feedingTubePreference} />
          {wishesInfo?.feedingTubeTimeLimit && <InfoItem label="Time Limit" value={wishesInfo.feedingTubeTimeLimit} />}
          {wishesInfo?.feedingTubeNotes && <InfoItem label="Notes" value={wishesInfo.feedingTubeNotes} />}

          <Text style={styles.sectionTitle}>Pain Management</Text>
          <InfoItem label="Preference" value={wishesInfo?.painManagementPreference} />
          {wishesInfo?.painManagementNotes && <InfoItem label="Notes" value={wishesInfo.painManagementNotes} />}

          <Text style={styles.sectionTitle}>IV Fluids</Text>
          <InfoItem label="Preference" value={wishesInfo?.ivFluidsPreference} />

          {wishesInfo?.additionalInstructions && (
            <>
              <Text style={styles.sectionTitle}>Additional Instructions</Text>
              <InfoItem label="Notes" value={wishesInfo.additionalInstructions} />
            </>
          )}

          <InfoItem label="Family Informed" value={wishesInfo?.familyInformed} />

          {!viewOnly && (
            <View style={styles.buttonContainer}>
              <TouchableOpacity style={[styles.button, styles.backToListButton]} onPress={() => (navigation as any).navigate('CarePreferences', { screen: 'CarePreferencesChecklist', params: { refreshKey: Date.now() } })}>
                <Text style={styles.buttonText}>Back</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.button, styles.deleteButton]} onPress={handleDelete}>
                <Text style={styles.buttonText}>Delete</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.button, styles.editButton]} onPress={handleEdit}>
                <Text style={styles.buttonText}>Edit</Text>
              </TouchableOpacity>
            </View>
          )}

          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#29BFC0' },
  header: { flexDirection: 'row', alignItems: 'center', padding: ms(15), paddingTop: ms(25), position: 'relative' },
  backButton: { position: 'absolute', left: ms(50), padding: ms(15), paddingTop: ms(50), zIndex: 1 },
  backIcon: { width: s(32), height: s(32), tintColor: '#fff' },
  topSection: { alignItems: 'center', paddingVertical: ms(20) },
  backgroundKey: { position: 'absolute', width: '85%', height: '85%', opacity: 0.1 },
  logo: { width: s(125), height: s(125), marginBottom: ms(16) },
  title: { fontSize: ms(24, 0.3), fontWeight: 'bold', color: '#FFFFFF', marginBottom: ms(8), paddingHorizontal: ms(30), textAlign: 'center' },
  infoContainer: { flex: 1, backgroundColor: '#F3F6F8', borderTopLeftRadius: ms(20), borderTopRightRadius: ms(20), paddingTop: ms(20) },
  scrollView: { paddingHorizontal: ms(16), paddingBottom: ms(120) },
  sectionTitle: { fontSize: ms(20, 0.3), fontWeight: 'bold', color: '#1C2C4F', marginBottom: ms(16), marginTop: ms(20) },
  infoItem: { marginBottom: ms(12) },
  label: { fontSize: ms(14, 0.3), fontWeight: '600', color: '#6B7280', marginBottom: ms(4) },
  value: { fontSize: ms(16, 0.3), color: '#1C2C4F' },
  separator: { height: s(1), backgroundColor: '#E5E5E5', marginTop: ms(12) },
  buttonContainer: { flexDirection: 'row', justifyContent: 'space-between', padding: ms(20), paddingBottom: ms(130), gap: ms(10) },
  button: { flex: 1, padding: ms(15), borderRadius: ms(25), alignItems: 'center' },
  backToListButton: { backgroundColor: '#29BFC0' },
  deleteButton: { backgroundColor: '#FF4444' },
  editButton: { backgroundColor: '#FF9F5A' },
  buttonText: { color: '#FFFFFF', fontSize: ms(18, 0.3), fontWeight: '600' },
  bottomPadding: { height: s(120) },
});
